/**
 * A document's own date (piece 22; T114, decision 0681). Every accounting
 * document is dated, tax and pricing read that date rather than today's, and a
 * date set more than thirty days back is an administrator's to set.
 *
 * The arithmetic is on calendar days written `YYYY-MM-DD`, so the rule reads
 * the same in the browser and on the server and never touches a clock.
 */
import { DAY_MS, calendarDayInZone } from '../zonedTime';

/** 🔒 How far back a person without the seeded Admin role may date a document (decision 0681). */
export const BACKDATE_WITHOUT_ADMIN_DAYS = 30;

/** The kinds the backdate rule holds on; the Bill is not among them, because its date is the vendor's (decision 0681). */
export const BACKDATE_HELD_KINDS = ['Quote', 'SalesOrder', 'Invoice', 'PurchaseOrder', 'ChangeOrder', 'WorkOrder', 'CreditNote'] as const;
export type BackdateHeldKind = (typeof BACKDATE_HELD_KINDS)[number];

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The number of whole days `earlier` stands before `later`, both `YYYY-MM-DD`; negative where it stands after. */
export function calendarDaysBetween(earlier: string, later: string): number {
  if (!DAY.test(earlier) || !DAY.test(later)) throw new Error(`a calendar day is written YYYY-MM-DD; got '${earlier}' and '${later}'`);
  return Math.round((Date.parse(`${later}T00:00:00Z`) - Date.parse(`${earlier}T00:00:00Z`)) / DAY_MS);
}

/**
 * 🔒 The day a document opens on: today in the zone of the person writing it,
 * never the machine's and never the server's in UTC (decisions 0261, 1030).
 */
export function documentDefaultDay(now: Date, zone: string): string {
  return calendarDayInZone(now, zone);
}

/**
 * 🔒 The day a payment, a deposit or a refund stores (piece 24, T807): the
 * typed day where one is given, else today in the zone named, never the clock.
 * The stored columns are calendar dates, so a fallback to the moment the write
 * ran handed the write an instant the column then dropped, and the day it
 * reasoned with was not the day any later window read.
 */
export function dayOrToday(typed: string | null | undefined, now: Date, zone: string): string {
  const day = typed?.trim().slice(0, 10) ?? '';
  return day !== '' ? day : documentDefaultDay(now, zone);
}

/** 🔒 The day a document's form opens on (decision 1030): the stored day on a saved document, else today in the viewer's own zone. */
export function documentOpeningDay(storedDay: string | null | undefined, now: Date, zone: string): string {
  return storedDay && DAY.test(storedDay) ? storedDay : documentDefaultDay(now, zone);
}

/** Whether a document date stands further back than a person without the Admin role may set. */
export function isHeldBackdate(documentDay: string, today: string): boolean {
  return calendarDaysBetween(documentDay, today) > BACKDATE_WITHOUT_ADMIN_DAYS;
}

/**
 * 🔒 The refusal a backdate past thirty days meets without the Admin role, in
 * one sentence naming the date and how far back it is; null where the date
 * stands, or where the person holds the role and the backdate is audited
 * instead (decision 0681).
 */
export function backdateRefusal(documentDay: string, today: string, holdsAdmin: boolean): string | null {
  if (!isHeldBackdate(documentDay, today) || holdsAdmin) return null;
  const days = calendarDaysBetween(documentDay, today);
  return `a document dated ${documentDay} is ${days} days back; only an administrator dates a document more than ${BACKDATE_WITHOUT_ADMIN_DAYS} days before today`;
}
